/**
 * PlayerResourceBackends — wires the core {@link ResourceManager} to the player runtime
 * subsystems so refcounted asset acquire/release drives the actual GPU/CPU resources.
 *
 * Classification + paths come from the loaded manifest tables. Texture is the dominant GPU
 * memory (and what get_render_stats measures); audio is evicted from the CPU cache. Materials /
 * json / binary are left unmanaged here (lazily handled / disposed at shutdown).
 */

import type { AssetKind, ResourceBackends } from "@engine/core";
import type { AssetSubsystem } from "../subsystems/AssetSubsystem";
import type { RenderSubsystem } from "../subsystems/RenderSubsystem";

export class PlayerResourceBackends implements ResourceBackends {
  constructor(
    private readonly assets: AssetSubsystem,
    private readonly render: RenderSubsystem,
  ) {}

  classify(guid: string): AssetKind | null {
    const m = this.assets.getManifest();
    if (!m) return null;
    if (m.assets.textures?.[guid]) return "texture";
    if (m.assets.audio?.[guid]) return "audio";
    return null;
  }

  async load(kind: AssetKind, guid: string): Promise<void> {
    const m = this.assets.getManifest();
    if (!m) return;
    if (kind === "texture") {
      // Warm the GPU texture via the renderer's resolver (manifest path → URL → upload).
      await this.render.acquireTexture(guid);
    } else if (kind === "audio") {
      const path = m.assets.audio?.[guid];
      if (path) await this.assets.loadAudio(guid, path);
    }
  }

  dispose(kind: AssetKind, guid: string): void {
    if (kind === "texture") {
      this.render.releaseTexture(guid);
      this.assets.evict(guid);
    } else if (kind === "audio") {
      this.assets.evict(guid);
    }
  }
}
